/**
 * PÁGINA DE RECUPERACIÓN DE CONTRASEÑA
 *
 * Envía enlace por email y opcionalmente OTP por WhatsApp.
 * Opción OTP: verificar código → mostrar Nueva contraseña / Confirmar → guardar → iniciar sesión (handoff).
 * Opción Link: el enlace lleva a /reset-password con token.
 * Preserva app y returnTo en todas las redirecciones.
 */

import React, { useState, FormEvent } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getBrandingConfig } from '@shared/config/brandingConfig';
import { resetPassword, verifyPhoneOTP } from '@shared/auth';
import { supabase } from '@shared/lib/supabaseClient';
import { Button } from '@shared/ui/shadcn/ui/button';
import { Alert, AlertDescription } from '@shared/ui/shadcn/ui/alert';
import { Input } from '@shared/ui/shadcn/ui/input';
import { Eye, EyeOff } from 'lucide-react';
import AuthLayout from '../components/AuthLayout';
import { useDynamicBranding } from '../hooks/useDynamicBranding';
import { handleHandoffRedirect } from './Login';
import { createSsoTrace } from '@shared/utils/ssoTrace';
import { notifyCoreAPILogin } from '@shared/services/coreAuthService';

type Step = 'request' | 'sent' | 'otp' | 'new_password';

const ForgotPasswordPage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const app = params.get('app') ?? '';
  const returnTo = params.get('returnTo') ?? '';
  const clientOriginParam = params.get('clientOrigin');

  useDynamicBranding(app);
  const branding = getBrandingConfig(app);

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [step, setStep] = useState<Step>('request');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [confirmPasswordFocused, setConfirmPasswordFocused] = useState(false);

  const loginUrl = `/login?app=${app}${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}${
    clientOriginParam ? `&clientOrigin=${encodeURIComponent(clientOriginParam)}` : ''
  }`;

  const handleRequestSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await resetPassword(email.trim(), {
        app: app || undefined,
        returnTo: returnTo || undefined,
        clientOrigin: clientOriginParam || undefined,
        phone: phone.trim() || undefined,
      });
      setStep(phone.trim() ? 'otp' : 'sent');
    } catch (err: any) {
      setStep('sent'); // mensaje genérico
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerify = async (e: FormEvent) => {
    e.preventDefault();
    if (otpCode.length !== 6) {
      setError('El código debe tener 6 dígitos');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await verifyPhoneOTP(phone.trim().replace(/\D/g, ''), otpCode);
      if (!result.success) {
        setError(result.error || 'Código inválido');
        setLoading(false);
        return;
      }
      setStep('new_password');
    } catch (err: any) {
      setError(err?.message || 'Error al verificar código');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setLoading(true);
    setError(null);
    const trace = createSsoTrace({ app: 'accounts', feature: 'RESET_PASSWORD_OTP' });
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        setError(updateError.message || 'Error al actualizar contraseña');
        setLoading(false);
        return;
      }
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) await notifyCoreAPILogin(session.access_token);

      const handoffSuccess = await handleHandoffRedirect(
        app || 'default',
        returnTo || null,
        (msg) => setError(msg),
        trace,
        clientOriginParam,
      );
      if (!handoffSuccess) {
        navigate(loginUrl);
      }
    } catch (err: any) {
      setError(err?.message || 'Error inesperado');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="w-full flex flex-col gap-6">
        <div className="text-center space-y-3">
          {app === 'houseme' ? (
            <div className="flex items-center justify-center gap-2">
              <img src={branding.assets.logo || '/icon-houseme-192x192.png'} alt="HouseMe" className="h-8 w-8" />
              <span className="text-lg lg:text-xl font-bold">
                <span style={{ color: `hsl(${branding.colors.primary})` }}>House</span>
                <span style={{ color: `hsl(${branding.colors.secondary || branding.colors.primary})` }}>Me</span>
              </span>
            </div>
          ) : app === 'empresa' ? (
            <div className="flex items-center justify-center gap-2">
              <img
                src={branding.assets.logo || '/icon-192x192.png'}
                alt="ThePlatform360"
                className="h-8 w-8"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (target.src !== window.location.origin + '/icon-192x192.png') {
                    target.src = '/icon-192x192.png';
                  }
                }}
              />
              <span className="text-lg lg:text-xl font-bold" style={{ color: `hsl(${branding.colors.primary})` }}>
                ThePlatform360
              </span>
            </div>
          ) : branding.assets.logo ? (
            <img src={branding.assets.logo} alt={branding.label} className="h-12 mx-auto" />
          ) : null}
          <div>
            <h1 className="text-xl font-semibold text-foreground">Recuperar contraseña</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {step === 'request' && 'Te enviaremos un enlace por email y, si indicas teléfono, un código por WhatsApp.'}
              {step === 'sent' && 'Revisa tu correo para el enlace de recuperación.'}
              {step === 'otp' && 'Ingresa el código de 6 dígitos que te enviamos por WhatsApp.'}
              {step === 'new_password' && 'Elige una nueva contraseña.'}
            </p>
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {step === 'request' && (
          <>
            <form onSubmit={handleRequestSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Input
                  id="email"
                  type="email"
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setEmailFocused(true)}
                  onBlur={() => setEmailFocused(false)}
                  required
                  autoComplete="email"
                  placeholder={emailFocused ? 'Escríbelo aquí' : ''}
                  className="placeholder:text-muted-foreground/40"
                />
              </div>
              <div className="space-y-1.5">
                <Input
                  id="phone"
                  type="tel"
                  inputMode="numeric"
                  label="Teléfono (opcional, para código por WhatsApp)"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                  placeholder="Ej: 8095551234"
                  className="placeholder:text-muted-foreground/40"
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full" size="lg">
                {loading ? 'Enviando...' : 'Enviar instrucciones'}
              </Button>
            </form>
            <div className="mt-4 text-center text-xs text-muted-foreground">
              <button
                type="button"
                onClick={() => navigate(loginUrl)}
                className="text-primary hover:underline font-medium"
              >
                Volver al login
              </button>
            </div>
          </>
        )}

        {step === 'sent' && (
          <div className="text-center space-y-4">
            <Alert>
              <AlertDescription className="text-center">
                Revisa tu bandeja de entrada. El enlace puede tardar unos minutos. Si no llega, revisa spam o solicita de nuevo.
              </AlertDescription>
            </Alert>
            <Button variant="ghost" onClick={() => navigate(loginUrl)} className="w-full">
              Volver al login
            </Button>
          </div>
        )}

        {step === 'otp' && (
          <>
            <form onSubmit={handleOtpVerify} className="space-y-4">
              <Input
                id="otp"
                type="text"
                inputMode="numeric"
                maxLength={6}
                label="Código de 6 dígitos"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                disabled={loading}
              />
              <Button type="submit" disabled={otpCode.length !== 6 || loading} className="w-full" size="lg">
                {loading ? 'Verificando...' : 'Verificar código'}
              </Button>
            </form>
            <p className="text-center text-xs text-muted-foreground">
              ¿Prefieres usar el enlace del correo? Ábrelo para restablecer la contraseña.
            </p>
            <Button variant="ghost" onClick={() => navigate(loginUrl)} className="w-full">
              Volver al login
            </Button>
          </>
        )}

        {step === 'new_password' && (
          <>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    label="Nueva contraseña"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={() => setPasswordFocused(false)}
                    autoComplete="new-password"
                    placeholder={passwordFocused ? 'Mínimo 6 caracteres' : ''}
                    disabled={loading}
                    className="pr-10 placeholder:text-muted-foreground/40"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="relative">
                  <Input
                    id="confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    label="Confirmar contraseña"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onFocus={() => setConfirmPasswordFocused(true)}
                    onBlur={() => setConfirmPasswordFocused(false)}
                    autoComplete="new-password"
                    placeholder={confirmPasswordFocused ? 'Repite la contraseña' : ''}
                    disabled={loading}
                    className="pr-10 placeholder:text-muted-foreground/40"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading || password.length < 6 || password !== confirmPassword} className="w-full" size="lg">
                {loading ? 'Guardando...' : 'Guardar e iniciar sesión'}
              </Button>
            </form>
          </>
        )}
      </div>
    </AuthLayout>
  );
};

export default ForgotPasswordPage;
